/**
 * The pages a sign-in and an org switch go back to (the Navigation review, D11), kept in sessionStorage across the
 * sign-in's round trip and an org switch's reload. This module imports nothing, so the sign-in and sign-out code can
 * use it without pulling in the menu.
 *
 * A kept address starts with /app/ and names a page under it (never /app itself), with no //, no backslash, no scheme
 * and no control character; anything else is ignored. The sign-in's page is kept RETURN_TTL_MS at most, so one an
 * abandoned sign-in left never outlasts it, and both are cleared on sign-out. So is the authRequest /login last showed
 * while signed out, which tells Twin's own from another client's.
 */

const RETURN_KEY = "forge.nav.returnTo";
const KEEP_KEY = "forge.nav.keepOnSwitch";
const OWN_REQUEST_KEY = "forge.nav.ownAuthRequest";
/** How long a sign-in's page is kept: the sign-in's round trip, with room to spare. */
export const RETURN_TTL_MS = 30 * 60_000;

const SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/**
 * An address a sign-in or an org switch may go back to: a page under /app/ (its path, before any query or fragment,
 * names more than /app), with no //, no backslash, no scheme and no control character. null for anything else.
 */
export function safeAppPath(raw: unknown): string | null {
  if (typeof raw !== "string" || raw.length > 2048) return null;
  if (!raw.startsWith("/app/") || raw.includes("//") || raw.includes("\\") || SCHEME.test(raw)) return null;
  for (let i = 0; i < raw.length; i++) {
    const c = raw.charCodeAt(i);
    if (c < 0x20 || c === 0x7f) return null; // a control character
  }
  // /app/ alone, or with only a query or a fragment, is the landing itself: going back there would land on the same
  // mounted page and show nothing (#1211 review, MEDIUM).
  const path = raw.split(/[?#]/)[0].replace(/\/+$/, "");
  if (path === "/app") return null;
  return raw;
}

function write(key: string, value: string | null): void {
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch {
    // Storage blocked: the person lands on the landing page instead, as before.
  }
}

function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Keeps the page a person had open across the sign-in's round trip (if it is one to go back to), for RETURN_TTL_MS. */
export function rememberReturnTo(path: string, now: number = Date.now()): void {
  const safe = safeAppPath(path);
  write(RETURN_KEY, safe ? `${now}|${safe}` : null);
}

/** The page to go back to after sign-in; null for none, or one kept longer than RETURN_TTL_MS. */
export function peekReturnTo(now: number = Date.now()): string | null {
  const raw = read(RETURN_KEY);
  if (!raw) return null;
  const at = raw.indexOf("|");
  const since = Number(raw.slice(0, at));
  if (at < 1 || !Number.isFinite(since) || now - since > RETURN_TTL_MS || now < since) return null;
  return safeAppPath(raw.slice(at + 1));
}

export function clearReturnTo(): void {
  write(RETURN_KEY, null);
}

/** Keeps the page an org switch may go back to (keptPageOf decides which), for the new org's landing to weigh. */
export function rememberKeptPage(kept: string | null): void {
  write(KEEP_KEY, safeAppPath(kept));
}

/** The page an org switch kept; null for none. */
export function peekKeptPage(): string | null {
  return safeAppPath(read(KEEP_KEY));
}

export function clearKeptPage(): void {
  write(KEEP_KEY, null);
}

/**
 * The authRequest this tab's /login last showed while signed out. Another client's can be kept here too, but Twin
 * starts its own only while signed out, so once the tab has a Twin session the last one kept is Twin's. A Back to its
 * page after the sign-in goes on to the app; one first shown while signed in is another client's (login.tsx).
 */
export function rememberOwnAuthRequest(id: string): void {
  write(OWN_REQUEST_KEY, id);
}

export function isOwnAuthRequest(id: string): boolean {
  return read(OWN_REQUEST_KEY) === id;
}

/** Forgets both pages and the kept authRequest, as a sign-out must: the next person here starts from the landing. */
export function forgetNavPages(): void {
  clearReturnTo();
  clearKeptPage();
  write(OWN_REQUEST_KEY, null);
}
